/**
 * RegionMap 라우터 — 선택된 광역권역의 시각 자산을 분기 렌더.
 *
 * 광역권역별 지도 형태가 다르므로(서울=단일 path, 경기=시별 합성, 인천·경상=추후) 각각의 컴포넌트로 분리.
 * 라우터는 isFullCoverage 기반으로 안내 영역(메시지 vs 범례)만 분기 처리.
 *
 * 안내 영역 분기:
 *  - isFullCoverage=true (예: 서울) → 메시지 ("전 지역 어디서든 수거 가능해요!")
 *  - isFullCoverage=false (예: 경기) → 범례 (전 지역 가능 / 일부 지역 가능 아이콘 + 라벨)
 *
 * 새 광역권역 추가 시:
 *  1. `maps/{광역권역}Map.tsx` 작성
 *  2. `constants/regionMaps.ts`의 REGION_MAPS에 등록 (Map 컴포넌트 + 메시지)
 */

import { REGION_MAPS, splitMessageByHighlight } from '../../constants/regionMaps'

import Legend from './Legend'
import { Message, MessageHighlight, Wrapper } from './styles'

interface RegionMapProps {
  /** 현재 선택된 광역권역 한글 이름 ('서울', '경기', '인천', '경상') */
  region: string
  /** 광역권역 레벨 전 지역 가능 여부 — 메시지 vs 범례 분기에 사용 */
  isFullCoverage: boolean
  /** 하위 광역권역 한글값 ('경기 북부' 등). null이면 전체. Map 컴포넌트가 캔버스 분기에 사용 */
  subRegion?: string | null
  /**
   * 시 라벨 클릭 시 호출. 인자는 백엔드 SiGunGu.name(한글).
   * page.tsx가 받아 해당 시군구 카드 펼침 + 스크롤 처리.
   */
  onLabelClick?: (sigunguName: string) => void
}

export default function RegionMap({ region, isFullCoverage, subRegion, onLabelClick }: RegionMapProps) {
  const meta = REGION_MAPS[region]

  // 시각 자산 없는 광역권역(예: 인천·경상 자료 수령 전) → 렌더 생략
  if (!meta) return null

  const { Map, message } = meta

  return (
    <Wrapper>
      <Map
        subRegion={subRegion}
        onLabelClick={onLabelClick}
      />
      {isFullCoverage && message && <FullCoverageMessage message={message} />}
      {!isFullCoverage && <Legend />}
    </Wrapper>
  )
}

/** isFullCoverage=true 광역권역 안내 메시지 — highlight 부분만 brand orange */
function FullCoverageMessage({ message }: { message: { text: string; highlight: string } }) {
  const { before, highlight, after } = splitMessageByHighlight(message)
  return (
    <Message>
      {before}
      <MessageHighlight>{highlight}</MessageHighlight>
      {after}
    </Message>
  )
}
